import { PLPURL, UpdateAction } from '@tvg/product-listing-url';
import { EUserAction } from 'client/config';
import React, { createContext, useContext, useRef } from 'react';
import { createStore } from 'zustand';
import { useStoreWithEqualityFn } from 'zustand/traditional';

export interface MainStateValues {
  plpURL: PLPURL;
  lastAction: EUserAction | null;
  firstCategory: string;
  firstURL: string;
  isHideVeryPayBanner?: boolean;
  children?: React.ReactNode;
}

export interface MainStateActions {
  updatePLPURL(update: UpdateAction): string;
  setPLPURL(plpurl: PLPURL): void;
  setLastAction(action: EUserAction): void;
  setIsHideVeryPayBanner(isHideVeryPayBanner: boolean): void;
}

export interface MainState extends MainStateValues, MainStateActions {}

const defaultState = {
  lastAction: null,
  isHideVeryPayBanner: false,
} as MainStateValues;

export const createMainStore = (init: Partial<MainStateValues>) =>
  createStore<MainState>()((set, get) => ({
    ...defaultState,
    ...init,
    firstCategory: init.plpURL?.category.id ?? '',
    firstURL: init.plpURL?.pathname ?? '',
    setLastAction: (action) => set({ lastAction: action }),
    setPLPURL: (plpURL) => set({ plpURL }),
    updatePLPURL: (update) => {
      const { plpURL } = get();
      return plpURL.setHref(update);
    },
    setIsHideVeryPayBanner: (isHideVeryPayBanner) => set({ isHideVeryPayBanner }),
  }));

const MainStoreContext = createContext<ReturnType<typeof createMainStore> | null>(null);

// eslint-disable-next-line react/function-component-definition
export const MainStoreProvider: React.FC<Partial<MainStateValues>> = ({ children, ...init }) => {
  const storeRef = useRef<ReturnType<typeof createMainStore>>();
  if (!storeRef.current) storeRef.current = createMainStore(init);

  return <MainStoreContext.Provider value={storeRef.current}>{children}</MainStoreContext.Provider>;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const equalityFn = (a: any, b: any) => a === b;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const defSelector = (s: any) => s as MainState;

export const useMainState = <T = MainState,>(selector?: (state: MainState) => T): T => {
  const store = useContext(MainStoreContext);
  if (!store) throw new Error('Missing Main Context Provider');
  return useStoreWithEqualityFn(store, selector ?? defSelector, equalityFn);
};
